import { useEffect, useState } from "react";
import type {
  LenderAccount,
  LenderContact,
  SubmissionStatus,
} from "@edge/shared";
import {
  ApiError,
  createSubmission,
  fetchLenderContacts,
  fetchLenders,
  type CreateSubmissionInput,
} from "../../../api";
import { ALL_STATUSES, STATUS_LABELS } from "../../../lib/submission-status";

interface AddSubmissionDialogProps {
  dealId: string;
  dealName: string;
  /** Throws on failure. */
  onCreated: () => Promise<void> | void;
  onCancel: () => void;
}

function todayInPacific(): string {
  // YYYY-MM-DD in Pacific time. The pitched_at column is a date string.
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date());
}

/**
 * "Add submission" form. Lender + (optional) contact + initial status +
 * pitched_at. Lender list loads once on mount; contact list lazy-loads
 * when a lender is picked, scoped via /api/lenders/:id/contacts.
 */
export function AddSubmissionDialog({
  dealId,
  dealName,
  onCreated,
  onCancel,
}: AddSubmissionDialogProps) {
  const [lenders, setLenders] = useState<LenderAccount[] | null>(null);
  const [contacts, setContacts] = useState<LenderContact[] | null>(null);
  const [contactsLoading, setContactsLoading] = useState(false);

  const [lenderId, setLenderId] = useState("");
  const [contactId, setContactId] = useState("");
  const [status, setStatus] = useState<SubmissionStatus>("pitched");
  const [pitchedAt, setPitchedAt] = useState<string>(todayInPacific);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load lender list once.
  useEffect(() => {
    let cancelled = false;
    fetchLenders()
      .then((r) => {
        if (!cancelled) setLenders(r.lenders);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(`Failed to load lenders: ${formatErr(err)}`);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Lazy-load contacts when lender changes.
  useEffect(() => {
    if (!lenderId) {
      setContacts(null);
      setContactId("");
      return;
    }
    let cancelled = false;
    setContactsLoading(true);
    setContacts(null);
    fetchLenderContacts(lenderId)
      .then((r) => {
        if (!cancelled) setContacts(r.contacts);
      })
      .catch(() => {
        if (!cancelled) setContacts([]); // graceful: empty list, free-text fallback
      })
      .finally(() => {
        if (!cancelled) setContactsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lenderId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const selectedLender = lenders?.find((l) => l.id === lenderId);
  const selectedContact = contacts?.find((c) => c.id === contactId);

  const canSave = Boolean(lenderId) && Boolean(selectedLender) && !running;

  const handleSave = async () => {
    if (!selectedLender) return;
    setError(null);
    setRunning(true);
    const input: CreateSubmissionInput = {
      deal_id: dealId,
      lender_account_id: selectedLender.id,
      lender_name: selectedLender.name,
      contact_id: selectedContact?.id ?? null,
      contact_name: selectedContact?.name ?? null,
      contact_role: selectedContact?.role ?? null,
      status,
      pitched_at: pitchedAt,
    };
    try {
      await createSubmission(input);
      await onCreated();
    } catch (err) {
      // Catch the 409 UNIQUE-constraint case from S.1's worker and surface it
      // inline instead of as a generic failure.
      if (err instanceof ApiError && err.code === "duplicate_submission") {
        setError(
          `${selectedLender.name} already has a submission on this deal.`,
        );
      } else {
        setError(`Save failed: ${formatErr(err)}`);
      }
      setRunning(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card submission-add-card">
        <div className="write-confirm-title">Add submission to {dealName}</div>
        <div className="submission-form-body">
          <label className="submission-form-field">
            <span className="submission-form-label">
              Lender <span className="submission-form-required">·required</span>
            </span>
            <select
              className="submission-form-input"
              value={lenderId}
              onChange={(e) => setLenderId(e.target.value)}
              disabled={!lenders}
              autoFocus
            >
              <option value="">
                {lenders ? "Pick a lender…" : "Loading lenders…"}
              </option>
              {lenders?.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>

          <label className="submission-form-field">
            <span className="submission-form-label">
              Contact <span className="submission-form-hint">·optional</span>
            </span>
            <select
              className="submission-form-input"
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              disabled={!lenderId || contactsLoading}
            >
              <option value="">
                {!lenderId
                  ? "(pick a lender first)"
                  : contactsLoading
                    ? "Loading contacts…"
                    : contacts && contacts.length === 0
                      ? "(no contacts for this lender)"
                      : "(no contact)"}
              </option>
              {contacts?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.role ? ` — ${c.role}` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="submission-form-field">
            <span className="submission-form-label">Initial status</span>
            <select
              className="submission-form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as SubmissionStatus)}
            >
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="submission-form-field">
            <span className="submission-form-label">Pitched date</span>
            <input
              type="date"
              className="submission-form-input"
              value={pitchedAt}
              onChange={(e) => setPitchedAt(e.target.value)}
            />
          </label>
        </div>

        {error && <div className="write-confirm-error">⚠ {error}</div>}

        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleSave}
            disabled={!canSave}
          >
            {running ? "WRITING…" : "ADD SUBMISSION"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) return `${err.code} (HTTP ${err.status})`;
  if (err instanceof Error) return err.message;
  return "unknown_error";
}
